import { useRef, useState, useEffect } from "react";

// Full-screen pinch-to-zoom + pan image for the mobile pallet photo viewer.
// touchAction:"none" disables native page scroll/zoom so our gestures own the surface.
export default function ZoomableImage({ src, alt, testId }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const st = useRef({ startDist: 0, startScale: 1, panStart: null, lastTap: 0, panning: false });

  useEffect(() => { setScale(1); setPos({ x: 0, y: 0 }); }, [src]);

  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const onTouchStart = (e) => {
    const t = e.touches;
    if (t.length === 2) {
      st.current.startDist = dist(t);
      st.current.startScale = scale;
      st.current.panStart = null;
    } else if (t.length === 1) {
      if (scale > 1) st.current.panStart = { x: t[0].clientX - pos.x, y: t[0].clientY - pos.y };
      const now = Date.now();
      if (now - st.current.lastTap < 300) {
        if (scale > 1) { setScale(1); setPos({ x: 0, y: 0 }); }
        else { setScale(2.5); }
        st.current.lastTap = 0;
      } else {
        st.current.lastTap = now;
      }
    }
  };

  const onTouchMove = (e) => {
    const t = e.touches;
    if (t.length === 2 && st.current.startDist) {
      const ns = Math.min(4, Math.max(1, st.current.startScale * (dist(t) / st.current.startDist)));
      setScale(ns);
      if (ns <= 1) setPos({ x: 0, y: 0 });
    } else if (t.length === 1 && scale > 1 && st.current.panStart) {
      st.current.panning = true;
      setPos({ x: t[0].clientX - st.current.panStart.x, y: t[0].clientY - st.current.panStart.y });
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length === 0) {
      st.current.panStart = null;
      st.current.startDist = 0;
      st.current.panning = false;
      if (scale <= 1) setPos({ x: 0, y: 0 });
    }
  };

  return (
    <div
      data-testid={testId}
      onClick={(e) => e.stopPropagation()}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        overflow: "hidden", touchAction: "none", maxWidth: "100vw", maxHeight: "82vh",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          maxWidth: "100vw", maxHeight: "82vh", objectFit: "contain",
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transition: st.current.panning ? "none" : "transform 0.12s ease-out",
          willChange: "transform", userSelect: "none",
        }}
      />
    </div>
  );
}
